import { useEffect, useRef, useState } from 'react';

/**
 * Общий кэш «id → сущность» для вкладок, которые дотягивают недостающие
 * поля объекта (имя камеры, зоны камеры и т.п.) по идентификатору,
 * встретившемуся в списке другого ресурса.
 *
 * Извлечено из четырёх одинаковых copy-paste реализаций (ТЗ, finding 5):
 * ViolationRulesTab, ViolationEventsTab, ChangeRequestsTab и
 * MyChangeRequestsTab держали идентичный набор — cameraCacheRef (Map) + cancelled
 * guard + useEffect, который считал unknownIds и параллельно догружал их.
 *
 * Поведение, которое обязано сохраниться при переносе:
 *  - запрашиваются только те id, которых ещё нет в кэше (Map переживает
 *    ре-рендеры и не бьёт по сети повторно за уже известным);
 *  - поздний ответ, пришедший после размонтирования компонента (или после
 *    того, как список id успел смениться), игнорируется — cancelled-флаг;
 *  - ошибка по одному конкретному id не должна ронять остальные — на нём
 *    кэшируется mapResult(null), а не отбрасывается весь Promise.all.
 */
export const useEntityCache = (ids, fetchOne, mapResult = (value) => value) => {
  const cacheRef = useRef(new Map());
  const [byId, setById] = useState({});

  useEffect(() => {
    const unknownIds = [...new Set((ids || []).filter(Boolean))]
      .filter((id) => !cacheRef.current.has(id));
    if (unknownIds.length === 0) return undefined;

    let cancelled = false;
    Promise.all(
      unknownIds.map((id) =>
        Promise.resolve(fetchOne(id))
          .then((result) => [id, mapResult(result)])
          .catch(() => [id, mapResult(null)])
      )
    ).then((pairs) => {
      if (cancelled) return;
      pairs.forEach(([id, value]) => cacheRef.current.set(id, value));
      setById(Object.fromEntries(cacheRef.current));
    });

    return () => { cancelled = true; };
  }, [ids, fetchOne, mapResult]);

  return { byId };
};

export default useEntityCache;
